<template>
  <div class="dossiers-overview">
    <dd-button
      v-if="mortgageId && officeIsVerified"
      :loading="loadingInformationRequest"
      class="mb-sm"
      @click="informationRequest"
      :disabled="!financierTPNumber"
    >
      Informatieverzoek doen
    </dd-button>
    <p
        v-else-if="!loading && mortgageId && !officeIsVerified"
    >
      Het kantoor van deze klant is niet gemachtigd om IA berichten te versturen.
    </p>
    <small v-if="!loading && mortgageId && !financierTPNumber" style="color:red;">
      Ga naar relaties en stel een tussenpersoonnummer in voor deze geldverstrekker.
    </small>
    <!-- Filters -->
    <div style="padding: 0 5px; line-height: 45px">
      <!-- Per page -->
      <div class="per-page" style="display: inline-block; margin-left: 5px">
        Per pagina
        <a-select
          v-bind:disabled="loading"
          v-model="perPage"
          style="width: 70px"
          @change="onPerPageChange"
        >
          <a-select-option :value="15">15</a-select-option>
          <a-select-option :value="30">30</a-select-option>
          <a-select-option :value="50">50</a-select-option>
        </a-select>
      </div>

      <!-- Pagination top -->
      <div v-if="displayPagination" style="margin: 10px 0">
        <laravel-pagination
          :data="currentRequestData"
          @pagination-change-page="loadPage"
        ></laravel-pagination>
      </div>
    </div>

    <!-- Table -->
    <dd-main-table
      :headers="getColumns"
      :rows="dossiers"
      :loading="loading"
      row-key="id"
      :has-select="false"
      action-width="100px"
    >
      <template v-slot:actions="{ row }">
        <a-button @click="openDossier(row)"> bekijk </a-button>
      </template>
    </dd-main-table>

    <!-- Pagination bottom -->
    <div v-if="displayPagination" style="margin-top: 10px">
      <laravel-pagination
        :data="currentRequestData"
        @pagination-change-page="loadPage"
      ></laravel-pagination>
    </div>

    <DossierModal ref="dossierModal" />
  </div>
</template>

<script>
import HDNDossiersApi from "~/api/hdn_dossiers";
import { Skeleton } from "vue-loading-skeleton";
import DossierModal from "~/modules/hdn/DossierDrawer.vue";
import { DateFormatEnum } from "~/enums";
import LastRecordCell from "~/modules/hdn/LastRecordCell.vue";
import MortgageCell from "~/modules/hdn/MortgageCell.vue";

export default {
  name: "AllHDNDossiersOverview",
  props: {
    mortgageId: {
      type: String,
      required: false,
    },
    onlyWithUnviewedRecords: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Skeleton,
    DossierModal,
  },
  mounted() {
    this.loadPage(1, true);
  },
  data() {
    return {
      DateFormatEnum,
      dossiers: [],
      currentRequestData: null,
      loading: false,
      loadingInformationRequest: false,
      page: 1,
      perPage: 15,
      orderBy: "created_at",
      order: "desc",
      officeIsVerified: false,
      financierTPNumber: null,
      columns: [
        {
          title: "Hypotheek",
          key: "mortgage",
          notIfMortgageGiven: true,
          typeComponent: MortgageCell,
          width: "300px",
        },
        {
          title: "Geldverstrekker",
          key: "receiver_company_name",
          width: "300px",
        },
        {
          title: "Laatste bericht",
          typeComponent: LastRecordCell,
          key: "last_record",
        },
      ],
    };
  },
  computed: {
    isLoaded() {
      return this.currentRequestData !== null;
    },
    displayPagination() {
      return this.isLoaded && this.hasDossiers && this.hasPages;
    },
    hasDossiers() {
      return this.dossiers.length > 0;
    },
    hasPages() {
      if (
        this.isLoaded &&
        this.currentRequestData.meta &&
        this.currentRequestData.meta.pagination
      ) {
        return this.currentRequestData.meta.pagination.total_pages > 1;
      }
      return false;
    },
    getColumns() {
      if (this.mortgageId) {
        return this.columns.filter((column) => !column.notIfMortgageGiven);
      }
      return this.columns;
    },
  },
  methods: {
    async informationRequest() {
      if (!this.mortgageId) {
        return;
      }

      try {
        this.loadingInformationRequest = true;
        let response = await HDNDossiersApi.sendInformationRequest(
          this.mortgageId
        );

        this.dossiers.unshift(response.data);
      } catch (e) {
        this.$notification["error"]({
          message: "Er ging iets mis",
          description: "Het is niet gelukt om dossiers te laden",
        });
      } finally {
        this.loadingInformationRequest = false;
      }
    },

    async loadPage(page = 1, forceReload = false) {
      if (page === this.page && !forceReload) return;

      this.loading = true;
      try {
        const params = {
          page,
          perPage: this.perPage,
          orderBy: this.orderBy,
          order: this.order,
          filter: null,
        };

        if (this.onlyWithUnviewedRecords) {
          params.filter = {
            onlyWithUnviewedRecords: true,
          };
        }

        if (this.mortgageId) {
          params.mortgage_id = this.mortgageId;
        }

        let response = await HDNDossiersApi.getDossiers(params);
        this.dossiers = response.data;
        this.currentRequestData = response;
        this.officeIsVerified = response.meta.officeIsVerified;
        this.financierTPNumber = response.meta.financierTPNumber;
        this.page = page;
      } catch (err) {
        this.$notification["error"]({
          message: "Er ging iets mis",
          description: "Het is niet gelukt om dossiers te laden",
        });
      } finally {
        this.loading = false;
      }
    },
    reloadPage() {
      this.loadPage(1, true);
    },
    onPerPageChange() {
      this.reloadPage();
    },
    openDossier(dossier) {
      this.$refs.dossierModal.viewDossier(dossier);
    },
  },
};
</script>
